import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { InsectaServiceBase } from '../insecta.base.service';
import {
  BartenderConfiguration,
  ICatalog,
  ICatalogContainer,
  ICatalogContainerAddRequest,
  ICatalogContainerInsectGrowthStageView,
  ICatalogContainerSubstrateView,
  ICatalogContainerUpdateRequest,
  ICatalogInsect,
  ICatalogInsectGrowthStageUpdateRequest,
  ICatalogInsectUpdateRequest,
  ICatalogInsectVariantUpdateRequest,
  ICatalogOrderItemType,
  ICatalogOrderItemTypeUpdateRequest,
  ICatalogProjectUpdateRequest,
  ICatalogSubstrate,
  ICatalogSubstrateAddRequest,
  ICatalogSubstrateUpdateRequest,
  ICatalogUpdateRequest,
  ICatalogWorkflowNotificationUpdateRequest,
  IContainerInsectGrowthStageUpdateRequest,
  IContainerSubstrateUpdateRequest,
  ILabelTemplate,
  IPrinters
} from '../insecta.models';

@Injectable({
  providedIn: 'root'
})
export class CatalogAdminService extends InsectaServiceBase {
  public addCatalogContainer(request: ICatalogContainerAddRequest): Observable<string> {
    return this.post<string>(`/admin/catalogs/${request.catalogId}/containers`, 'v1', request);
  }

  public addCatalogSubstrate(request: ICatalogSubstrateAddRequest): Observable<string> {
    return this.post<string>(`/admin/catalogs/${request.catalogId}/substrates`, 'v1', request);
  }

  public getCatalogByLocation(id: string): Observable<ICatalog> {
    return this.get<ICatalog>(`/admin/catalogs/${id}`, 'v1');
  }

  public getCatalogContainer(catalogId: string): Observable<ICatalogContainer[]> {
    return this.get<ICatalogContainer[]>(`/admin/catalogs/${catalogId}/containers`, 'v1').pipe(
      map((results) =>
        [...results].sort((a: ICatalogContainer, b: ICatalogContainer) =>
          a.isActive == b.isActive ? (a.container.name > b.container.name ? 1 : -1) : a.isActive > b.isActive ? 1 : -1
        )
      )
    );
  }

  public getCatalogContainerInsectGrowthStages(catalogId: string, containerId: string): Observable<ICatalogContainerInsectGrowthStageView[]> {
    return this.get<ICatalogContainerInsectGrowthStageView[]>(`/admin/catalogs/${catalogId}/containerinsectgrowthstages/${containerId}`, 'v1');
  }

  public getCatalogContainerSubstrates(catalogId: string, containerId: string): Observable<ICatalogContainerSubstrateView[]> {
    return this.get<ICatalogContainerSubstrateView[]>(`/admin/catalogs/${catalogId}/containersubstrates/${containerId}`, 'v1');
  }

  public getCatalogInsects(catalogId: string): Observable<ICatalogInsect[]> {
    return this.get<ICatalogInsect[]>(`/admin/catalogs/${catalogId}/insects`, 'v1').pipe(
      map((results) =>
        [...results].sort((a: ICatalogInsect, b: ICatalogInsect) =>
          a.isActive == b.isActive ? (a.insect.scientificName > b.insect.scientificName ? 1 : -1) : a.isActive > b.isActive ? 1 : -1
        )
      )
    );
  }

  public getCatalogOrderItemType(id: string): Observable<ICatalogOrderItemType[]> {
    return this.get<ICatalogOrderItemType[]>(`/admin/catalogs/${id}/orderitemtypes`, 'v1');
  }

  public getCatalogSubstrate(catalogId: string): Observable<ICatalogSubstrate[]> {
    return this.get<ICatalogSubstrate[]>(`/admin/catalogs/${catalogId}/substrates`, 'v1').pipe(
      map((results) =>
        [...results].sort((a: ICatalogSubstrate, b: ICatalogSubstrate) =>
          a.isActive == b.isActive ? (a.substrate.name > b.substrate.name ? 1 : -1) : a.isActive > b.isActive ? 1 : -1
        )
      )
    );
  }

  public updateCatalog(request: ICatalogUpdateRequest): Observable<any> {
    return this.put(`/admin/catalogs/${request.id}`, 'v1', request);
  }

  public updateCatalogContainer(catalogId: string, request: ICatalogContainerUpdateRequest): Observable<void> {
    return this.put<void>(`/admin/catalogs/${catalogId}/containers/${request.id}`, 'v1', request);
  }

  public updateCatalogContainerInsectGrowthStage(catalogId: string, request: IContainerInsectGrowthStageUpdateRequest): Observable<void> {
    return this.put<void>(`/admin/catalogs/${catalogId}/containerinsectgrowthstages/${request.id}`, 'v1', request);
  }

  public updateCatalogContainerSubstrate(catalogId: string, request: IContainerSubstrateUpdateRequest): Observable<void> {
    return this.put<void>(`/admin/catalogs/${catalogId}/containersubstrates/${request.id}`, 'v1', request);
  }

  public updateCatalogInsectGrowthStage(catalogId: string, request: ICatalogInsectGrowthStageUpdateRequest): Observable<void> {
    return this.put<void>(`/admin/catalogs/${catalogId}/growthstages/${request.id}`, 'v1', request);
  }

  public updateCatalogInsectVariant(catalogId: string, request: ICatalogInsectVariantUpdateRequest): Observable<void> {
    return this.put<void>(`/admin/catalogs/${catalogId}/variants/${request.id}`, 'v1', request);
  }

  public updateCatalogOrderItemType(catalogId: string, orderItemTypeId: string, request: ICatalogOrderItemTypeUpdateRequest): Observable<void> {
    return this.put<void>(`/admin/catalogs/${catalogId}/orderitemtypes/${orderItemTypeId}`, 'v1', request);
  }

  public updateCatalogProject(catalogId: string, request: ICatalogProjectUpdateRequest): Observable<void> {
    return this.put<void>(`/admin/catalogs/${catalogId}/projects/${request.id}`, 'v1', request);
  }

  public updateCatalogWorkflowNotification(catalogId: string, request: ICatalogWorkflowNotificationUpdateRequest): Observable<void> {
    return this.put<void>(`/admin/catalogs/${catalogId}/notifications/${request.id}`, 'v1', request);
  }

  public updateCatalogSubstrate(catalogId: string, request: ICatalogSubstrateUpdateRequest): Observable<void> {
    return this.put<void>(`/admin/catalogs/${catalogId}/substrates/${request.id}`, 'v1', request);
  }
  public updateCatalogInsect(catalogId: string, request: ICatalogInsectUpdateRequest): Observable<void> {
    return this.put<void>(`/admin/catalogs/${catalogId}/insects/${request.id}`, 'v1', request);
  }
  public getPrinters(catalogId: string): Observable<IPrinters> {
    return this.get<IPrinters>(`/bartender/catalogs/${catalogId}/printers`, 'v1');
  }

  public getAllLabels(catalogId: string): Observable<ILabelTemplate[]> {
    return this.get<ILabelTemplate[]>(`/bartender/catalogs/${catalogId}/labels`, 'v1');
  }

  public getBartenderConfig(): Observable<BartenderConfiguration> {
    return this.get<BartenderConfiguration>('/admin/catalogs/bartenderconfig', 'v1');
  }

  public printInstanceLabels(request: {
    orderItemInstanceIds: string[];
    printer: string;
    label: string;
    libraryId: string;
  }): Observable<{ message: string; filePaths: string[] }> {
    return this.post<{ message: string; filePaths: string[] }>('/labels/instances', 'v1', request);
  }
}
